import { Injectable, Injector } from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';

import { AuthService } from '../services/auth.service';
import { AgencyContextService } from '../services/agency-context.service';
import { catchError, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';

const READ_METHODS = ['GET', 'HEAD', 'OPTIONS'];

// Escritas permitidas em "Ver como" — iguais às do backend (jwt.strategy.ts):
// sair dela, terminar sessão, preferências e POSTs que só leem (filtros no body).
const ALLOWED_WRITES_WHILE_IMPERSONATING: RegExp[] = [
    /^\/impersonation\/stop\/?$/,
    /^\/logout\/?$/,
    /^\/auth\/user-data-pref\/?$/,
    /^\/menus\/check\/?$/,
    /^\/properties\/all\/[^/]+\/?$/,
    /^\/properties\/total-filters\/[^/]+\/?$/,
    /^\/contacts\/properties-by-preferences\/[^/]+\/?$/,
    /^\/contacts\/exist-contact(\/details)?\/?$/,
];

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
    constructor(
        private authService: AuthService, private router: Router, private translate: TranslateService, private cookieService: CookieService,
        private injector: Injector
    ) { }

    intercept(
        req: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {



        if (req.url.indexOf('/refresh') > -1) {
            return next.handle(req);
        }
        // "Ver como" é só de leitura: nem chega a enviar o pedido de escrita.
        if (this.isBlockedWhileImpersonating(req)) {
            const message = 't-impersonation-read-only';
            // Obtido aqui e não no construtor: injetá-lo no interceptor cria
            // uma dependência circular em HTTP_INTERCEPTORS (NG0200).
            this.injector.get(ToastrService).warning(this.translate.instant(message));
            return throwError(message);
        }
        if (req.url.indexOf('.json') > -1 && req.url.indexOf('/languages') > -1) {
            return this.handle404Translate(req, next);
        }

        return next.handle(req).pipe(
            catchError((err) => {
                if (err.status == 401) {
                    return this.handle401Error(req, next, err);
                } else {
                    if (this.isCmiRequest(req)) {
                        // CMI endpoints expose structured revision/overflow data
                        // that the editor uses to select the affected field.
                        return throwError(err);
                    }
                    // if (req.url.indexOf('/login')) {
                    //     return throwError(err);
                    // }
                    const error = err.error.message || err.statusText;
                    return throwError(error);
                }
            })
        );
    }

    /**
     * check refresh if Invalid user token
     * @param req 
     * @param next 
     * @param originalError 
     * @returns 
     */
    private handle401Error(
        req: HttpRequest<any>,
        next: HttpHandler,
        originalError: any
    ) {
        return this.authService.refreshCookie().pipe(
            switchMap(() => {
                return next.handle(req);
            }),
            catchError((err) => {
                if (this.isCmiRequest(req) && err.status !== 401) {
                    return throwError(err);
                }
                // A sessão acabou: o contexto global de agência acaba com ela.
                this.injector.get(AgencyContextService).clear();
                this.authService.removeUserToLocalStorage();
                if (!this.authService.returnUrl)
                    this.router.navigate(['/login']);
                else
                    this.router.navigate(['login'], { queryParams: { returnUrl: this.authService.returnUrl } });
                const error = err.error.message || err.statusText;
                return throwError(error);
            })
        );
    }

    private isBlockedWhileImpersonating(req: HttpRequest<any>): boolean {
        // Nas entidades em que o servidor deixa escrever em "Ver como" não há bloqueio.
        if (!this.authService.isImpersonatingReadOnly) return false;
        if (READ_METHODS.includes(req.method?.toUpperCase())) return false;
        if (req.url.indexOf(environment.apiUrl) !== 0) return false;
        const path = req.url.substring(environment.apiUrl.length).split('?')[0];
        return !ALLOWED_WRITES_WHILE_IMPERSONATING.some((pattern) => pattern.test(path));
    }

    private isCmiRequest(req: HttpRequest<any>): boolean {
        return req.url.indexOf('/mediation-agreement/templates') > -1 ||
            req.url.indexOf('/mediation-agreement/cmi') > -1 ||
            // Página pública do proprietário: precisa de distinguir link
            // inválido de link expirado, e isso vem no corpo do erro.
            req.url.indexOf('/cmi-share') > -1 ||
            // Portal do proprietário (público) e o seu lado de gestão: mesma
            // razão — a chave do erro (link expirado, pedido já submetido…)
            // tem de chegar inteira ao ecrã.
            req.url.indexOf('/owner-portal') > -1 ||
            // Ficha de visita virtual (pública): "já respondida" e "rubrica
            // inválida" chegam como chaves no corpo do erro.
            req.url.indexOf('/visit-form/virtual') > -1;
    }

    /**
     * check if translate exists
     * @param req 
     * @param next 
     * @returns 
     */
    private handle404Translate(
        req: HttpRequest<any>,
        next: HttpHandler
    ) {
        return next.handle(req).pipe(
            catchError((err) => {
                if (err.status == 404) {
                    this.cookieService.delete('lang');
                    this.cookieService.set('lang', 'en');
                    return this.translate.use('en');
                } else {
                    this.cookieService.delete('lang');
                    this.authService.removeUserToLocalStorage();
                    if (!this.authService.returnUrl)
                        this.router.navigate(['/login']);
                    else
                        this.router.navigate(['login'], { queryParams: { returnUrl: this.authService.returnUrl } });
                    const error = err.error.message || err.statusText;
                    return throwError(error);
                }
            })
        );
    }
}
